
import React from "react";

const money = (value) =>
  Number(value || 0).toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const firstNumber = (object, keys) => {
  for (const key of keys) {
    const value = object?.[key];

    if (
      value !== null &&
      value !== undefined &&
      value !== "" &&
      Number.isFinite(Number(value))
    ) {
      return Number(value);
    }
  }

  return null;
};

const getProductPrice = (depositAmount, depositPercent) => {
  const amount = Number(depositAmount);
  const percent = Number(depositPercent);

  if (
    depositAmount === null ||
    depositAmount === undefined ||
    depositAmount === "" ||
    !Number.isFinite(amount) ||
    !Number.isFinite(percent) ||
    percent <= 0
  ) {
    return null;
  }

  return amount / (percent / 100);
};

function PaymentScale({
  title,
  description,
  totalPrice,
  productPrice,
  deliveryPrice,
  additionalPrice,
  paid,
  thresholdPercent,
  palette,
}) {
  const validTotal = Number.isFinite(totalPrice) && totalPrice > 0;
  const validProduct =
    Number.isFinite(productPrice) && productPrice > 0;

  const paidAmount = Number.isFinite(Number(paid))
    ? Math.max(Number(paid), 0)
    : 0;

  const paidPercent = validTotal
    ? Math.min((paidAmount / totalPrice) * 100, 100)
    : 0;

  const threshold = Number(thresholdPercent);
  const validThreshold =
    validProduct &&
    Number.isFinite(threshold) &&
    threshold > 0 &&
    threshold <= 100;

  // Порог считается только от стоимости товара.
  const thresholdAmount = validThreshold
    ? productPrice * (threshold / 100)
    : 0;

  const thresholdPosition = validTotal
    ? Math.min((thresholdAmount / totalPrice) * 100, 100)
    : 0;

  const segments = [
    {
      name: "Товар",
      amount: Math.max(productPrice || 0, 0),
      color: palette.product,
    },
    {
      name: "Доставка",
      amount: Math.max(deliveryPrice || 0, 0),
      color: palette.delivery,
    },
    {
      name: "Дополнительные расходы",
      amount: Math.max(additionalPrice || 0, 0),
      color: palette.additional,
    },
  ];

  const segmentTotal = segments.reduce(
    (sum, segment) => sum + segment.amount,
    0
  );

  // Сегменты строятся пропорционально общей стоимости.
  // Небольшая разница из-за округления компенсируется
  // в последнем сегменте.
  const normalizedSegments = segments.map((segment, index) => {
    const amount =
      index === segments.length - 1 && validTotal
        ? Math.max(totalPrice - segments
            .slice(0, index)
            .reduce((sum, item) => sum + item.amount, 0), 0)
        : segment.amount;

    return {
      ...segment,
      amount,
      width: validTotal ? (amount / totalPrice) * 100 : 0,
    };
  });

  const thresholdReached =
    validThreshold && paidAmount >= thresholdAmount;

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>

        <span
          className={`rounded-full px-3 py-1 text-sm font-semibold ${
            thresholdReached
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          {thresholdReached
            ? "Минимум для производства достигнут"
            : validThreshold
              ? `До порога: ¥${money(
                  Math.max(thresholdAmount - paidAmount, 0)
                )}`
              : "Порог не задан"}
        </span>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-3">
        <div>
          <p className="text-sm text-gray-500">Уже оплачено</p>
          <p className="text-xl font-bold" style={{ color: palette.product }}>
            ¥{money(paidAmount)}
          </p>
          <p className="text-sm text-gray-500">
            {validTotal ? `${paidPercent.toFixed(1)}% от заказа` : "Нет данных"}
          </p>
        </div>

        <div className="text-right">
          <p className="text-sm text-gray-500">Общая стоимость заказа</p>
          <p className="text-xl font-bold text-gray-900">
            {validTotal ? `¥${money(totalPrice)}` : "Нет данных"}
          </p>
          {validTotal && (
            <p className="text-sm text-gray-500">
              Остаток: ¥{money(Math.max(totalPrice - paidAmount, 0))}
            </p>
          )}
        </div>
      </div>

      {validTotal && (
        <>
          <div className="relative pb-11 pt-2">
            <div
              className="relative flex h-7 w-full overflow-visible rounded-full"
              role="group"
              aria-label={`Состав стоимости: ${title}`}
            >
              {normalizedSegments.map((segment) => (
                <div
                  key={segment.name}
                  className="group relative h-7 cursor-help first:rounded-l-full last:rounded-r-full"
                  style={{
                    width: `${segment.width}%`,
                    backgroundColor: segment.color,
                  }}
                  tabIndex={0}
                  aria-label={`${segment.name}: ¥${money(segment.amount)}`}
                >
                  <div className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 hidden w-max max-w-64 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg group-hover:block group-focus:block">
                    <div className="font-semibold">{segment.name}</div>
                    <div>¥{money(segment.amount)}</div>
                    <div>
                      {((segment.amount / totalPrice) * 100).toFixed(1)}% от заказа
                    </div>
                  </div>
                </div>
              ))}

              {/* Неоплаченная часть: серый слой поверх сегментов. */}
              <div
                className="pointer-events-none absolute inset-y-0 right-0 rounded-r-full bg-gray-200/90"
                style={{ left: `${paidPercent}%` }}
              />
            </div>

            {/* Порог производства, рассчитанный только от товара. */}
            {validThreshold && (
              <div
                className="absolute top-0 z-20"
                style={{
                  left: `${thresholdPosition}%`,
                  transform:
                    thresholdPosition > 92
                      ? "translateX(-100%)"
                      : thresholdPosition < 8
                        ? "translateX(0)"
                        : "translateX(-50%)",
                }}
              >
                <div
                  className="mx-auto h-9 w-0.5 bg-black"
                  style={{
                    marginRight: thresholdPosition > 92 ? 0 : undefined,
                    marginLeft: thresholdPosition < 8 ? 0 : undefined,
                  }}
                />
                <div
                  className="mt-1 whitespace-nowrap rounded bg-white px-1 text-xs font-semibold text-gray-800 shadow-sm"
                >
                  {threshold}% · ¥{money(thresholdAmount)}
                </div>
              </div>
            )}
          </div>

          <div className="mb-3 flex flex-wrap justify-between gap-2 text-sm text-gray-600">
            <span>
              Оплачено: <strong>{paidPercent.toFixed(1)}%</strong>
            </span>
            <span>
              Не оплачено:{" "}
              <strong>
                {Math.max(100 - paidPercent, 0).toFixed(1)}%
              </strong>
            </span>
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-2 border-t border-gray-100 pt-3">
            {normalizedSegments.map((segment) => (
              <div key={segment.name} className="flex items-center gap-2 text-sm">
                <span
                  className="h-3 w-3 rounded-sm"
                  style={{ backgroundColor: segment.color }}
                />
                <span className="text-gray-600">
                  {segment.name}:{" "}
                  <strong className="text-gray-900">
                    ¥{money(segment.amount)}
                  </strong>
                </span>
              </div>
            ))}
          </div>

          {validThreshold && (
            <p className="mt-3 text-xs text-gray-500">
              Порог производства — {threshold}% от стоимости товара
              (¥{money(productPrice)}), без доставки и дополнительных расходов.
            </p>
          )}
        </>
      )}

      {!validTotal && (
        <p className="text-sm text-amber-700">
          Не удалось определить общую стоимость заказа. Проверьте поля цены
          в объекте заказа.
        </p>
      )}
    </section>
  );
}

export default function OrderPaymentInfographic({
  order,
  clientPaid,
  ourPaid,
}) {
  if (!order) return null;

  const depositPercent = Number(order.production_deposit_percent);

  // Цена товара восстанавливается из производственного депозита.
  const clientProductPrice = getProductPrice(
    order.client_production_deposit_amount,
    depositPercent
  );

  const ourProductPrice = getProductPrice(
    order.our_production_deposit_amount,
    depositPercent
  );

  // Общие цены заказа — товар + доставка + дополнительные расходы.
  const clientTotal = firstNumber(order, [
    "total_client_price",
    "client_total_price",
  ]);

  const ourTotal = firstNumber(order, [
    "total_internal_price",
    "internal_total_price",
    "total_factory_price",
  ]);

  // Подставь фактическое имя поля, если оно отличается.
  const clientDelivery = firstNumber(order, [
    "client_delivery_cost",
    "delivery_client_price",
    "delivery_cost",
    "delivery_price",
  ]) ?? 0;

  const ourDelivery = firstNumber(order, [
    "our_delivery_cost",
    "internal_delivery_cost",
    "delivery_internal_price",
    "factory_delivery_cost",
  ]) ?? 0;

  // Дополнительные расходы определяются из общей цены.
  const clientAdditional =
    clientTotal !== null && clientProductPrice !== null
      ? Math.max(
          clientTotal - clientProductPrice - clientDelivery,
          0
        )
      : 0;

  const ourAdditional =
    ourTotal !== null && ourProductPrice !== null
      ? Math.max(ourTotal - ourProductPrice - ourDelivery, 0)
      : 0;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-gray-900">
          Оплата заказа
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Шкала показывает общую стоимость заказа и прогресс оплаты.
          Наведите курсор на цветной сегмент, чтобы увидеть его сумму.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <PaymentScale
          title="Оплата клиента"
          description="Клиент → нам"
          totalPrice={clientTotal}
          productPrice={clientProductPrice}
          deliveryPrice={clientDelivery}
          additionalPrice={clientAdditional}
          paid={clientPaid}
          thresholdPercent={depositPercent}
          palette={{
            product: "#dc2626",
            delivery: "#f87171",
            additional: "#fecaca",
          }}
        />

        <PaymentScale
          title="Наша оплата фабрике"
          description="Мы → фабрике"
          totalPrice={ourTotal}
          productPrice={ourProductPrice}
          deliveryPrice={ourDelivery}
          additionalPrice={ourAdditional}
          paid={ourPaid}
          thresholdPercent={depositPercent}
          palette={{
            product: "#16a34a",
            delivery: "#4ade80",
            additional: "#bbf7d0",
          }}
        />
      </div>
    </div>
  );
}
